/** Downscale/re-encode receipt photos before extract + offline queue. */

export const RECEIPT_MAX_EDGE = 2048;
export const RECEIPT_JPEG_QUALITY = 0.8;
/** Skip work when already small enough (bytes). */
export const RECEIPT_SKIP_UNDER_BYTES = 500_000;

const IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
]);

function isCompressibleImage(file: File): boolean {
    if (IMAGE_TYPES.has(file.type)) return true;
    return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}

function jpegName(name: string): string {
    const base = name.replace(/\.[^/.]+$/, '') || 'receipt';
    return `${base}.jpg`;
}

/**
 * Resize long edge to maxEdge and JPEG-encode.
 * PDFs / non-images / decode failures / larger-than-original results keep the input File.
 */
export async function compressReceiptImage(
    file: File,
    {
        maxEdge = RECEIPT_MAX_EDGE,
        quality = RECEIPT_JPEG_QUALITY,
        skipUnderBytes = RECEIPT_SKIP_UNDER_BYTES,
    }: {
        maxEdge?: number;
        quality?: number;
        skipUnderBytes?: number;
    } = {}
): Promise<File> {
    if (!isCompressibleImage(file)) return file;

    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        // HEIC / exotic codecs often fail here — keep original for backend compose.
        return file;
    }

    try {
        const longest = Math.max(bitmap.width, bitmap.height);
        const alreadySmall =
            file.size <= skipUnderBytes && longest <= maxEdge;
        if (alreadySmall && file.type === 'image/jpeg') {
            return file;
        }

        const scale = longest > maxEdge ? maxEdge / longest : 1;
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, width, height);

        const blob = await new Promise<Blob | null>(resolve => {
            canvas.toBlob(resolve, 'image/jpeg', quality);
        });
        if (!blob || blob.size >= file.size) return file;

        return new File([blob], jpegName(file.name), {
            type: 'image/jpeg',
            lastModified: Date.now(),
        });
    } finally {
        bitmap.close();
    }
}

export async function compressReceiptImages(files: File[]): Promise<File[]> {
    const out: File[] = [];
    // Sequential — less peak memory on phones when adding several pages.
    for (const file of files) {
        out.push(await compressReceiptImage(file));
    }
    return out;
}
